import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Snowflake,
  Sparkles,
  Activity,
  Package as PackageIcon,
  BadgePercent,
  Info,
  UsersRound,
  ArrowRight,
  Dumbbell,
  Smile,
  Zap,
  Eye,
  Hexagon,
  Waves,
  HeartPulse,
  ShieldCheck,
  Clock,
} from 'lucide-react';
import BrandMark from '../components/BrandMark';
import { SERVICES, PACKAGE_TIERS, REFERRAL, money, moneyRange } from '../lib/services';

const CATEGORY_ICONS = {
  aesthetic: Sparkles,
  orthopedic: Activity,
};

export default function Services() {
  const navigate = useNavigate();

  const aesthetics = SERVICES.filter((s) => s.category === 'aesthetic');
  const orthopedic = SERVICES.filter((s) => s.category === 'orthopedic');

  return (
    <div className="min-h-screen frost-bg relative">
      <div className="relative z-10 min-h-screen flex flex-col items-center px-4 sm:px-6 py-8">
        <div className="w-full max-w-6xl">
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => navigate('/')}
              className="pill-btn inline-flex items-center gap-2 h-12 px-5 rounded-full bg-slate-900/70 border border-white/10 text-slate-200 font-semibold hover:bg-slate-800 active:scale-95"
              data-testid="services-back-button"
            >
              <ArrowLeft className="w-4 h-4" />
              Home
            </button>
            <BrandMark size="sm" showAddress={false} />
            <div className="w-[92px]" />
          </div>

          {/* Hero */}
          <div className="glass-card rounded-[2.5rem] p-8 sm:p-12 reveal text-center">
            <p className="text-xs tracking-[0.3em] uppercase text-blue-400 font-bold">
              Menu
            </p>
            <h1 className="font-display text-4xl sm:text-6xl text-white tracking-tight mt-3">
              Services, Pricing &amp; Packages
            </h1>
            <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto">
              Precision cryotherapy — aesthetics and orthopedic. Transparent price
              ranges that account for treatment depth and duration.
            </p>
          </div>

          {/* What We Treat (marketing / treatment focus) */}
          <WhatWeTreat className="mt-10 reveal reveal-delay-1" />

          {/* Aesthetics */}
          <ServiceSection
            title="Cryo — Aesthetics"
            icon={Sparkles}
            services={aesthetics}
            className="mt-10 reveal reveal-delay-2"
          />

          {/* Orthopedic */}
          <ServiceSection
            title="Cryo — Orthopedic"
            icon={Activity}
            services={orthopedic}
            className="mt-10 reveal reveal-delay-3"
          />

          {/* Packages */}
          <section className="mt-10 reveal reveal-delay-3">
            <div className="flex items-center gap-3 mb-5 px-2">
              <PackageIcon className="w-5 h-5 text-blue-400" />
              <h2 className="font-display text-2xl sm:text-3xl text-white">
                Session Packages
              </h2>
            </div>
            <div className="glass-card rounded-3xl p-8">
              <p className="text-slate-300 text-[15px] leading-relaxed max-w-3xl">
                Buy in advance and save. The prices below show your total
                bundled cost at each service&rsquo;s base rate — the discount is
                already applied.
              </p>

              <PackagePricingTable services={SERVICES} tiers={PACKAGE_TIERS.filter((t) => t.sessions > 1)} />

              <div className="mt-6 flex items-start gap-3 p-4 rounded-2xl bg-blue-500/10 border border-blue-500/20">
                <Info className="w-4 h-4 text-blue-300 shrink-0 mt-0.5" />
                <p className="text-sm text-slate-200 leading-relaxed">
                  Package sessions are tracked on your client account. Prices
                  shown are at the base rate; final bundle cost may vary within
                  each service&rsquo;s range depending on treatment depth.
                </p>
              </div>
            </div>
          </section>

          {/* Referral Program */}
          <section className="mt-10 reveal reveal-delay-3" data-testid="services-referral-section">
            <div className="flex items-center gap-3 mb-5 px-2">
              <UsersRound className="w-5 h-5 text-blue-400" />
              <h2 className="font-display text-2xl sm:text-3xl text-white">
                Referral Program
              </h2>
            </div>
            <div className="glass-card rounded-3xl p-8">
              <div className="flex flex-col md:flex-row md:items-center gap-8">
                <div className="flex-1">
                  <p className="font-display text-5xl text-white tracking-tight">
                    Refer <span className="text-blue-400" data-testid="referral-count">{REFERRAL.referralsPerYear}</span> · Earn <span className="text-blue-400" data-testid="referral-commission">{REFERRAL.commissionPct}%</span>
                  </p>
                  <p className="mt-4 text-slate-300 text-[15px] leading-relaxed max-w-2xl">
                    Refer <strong>{REFERRAL.referralsPerYear} new clients per year</strong> and unlock a
                    <strong> {REFERRAL.commissionPct}% lifetime commission</strong> on every dollar
                    they spend with KRYOTEKS. Once qualified, your commission
                    continues for as long as they remain a client.
                  </p>
                  <ul className="mt-5 space-y-2 text-sm text-slate-300">
                    <li className="flex items-start gap-2.5">
                      <BadgePercent className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                      Ask any staff member to enroll or check your referral status.
                    </li>
                    <li className="flex items-start gap-2.5">
                      <BadgePercent className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                      Referrals must sign a waiver and complete at least one paid session to count.
                    </li>
                    <li className="flex items-start gap-2.5">
                      <BadgePercent className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                      Commission accrues monthly and applies to future sessions or packages.
                    </li>
                  </ul>
                </div>
                <div className="md:w-72 shrink-0 rounded-3xl border border-blue-500/30 bg-blue-500/10 p-6 text-center">
                  <p className="text-xs tracking-[0.2em] uppercase text-blue-300 font-bold">Terms</p>
                  <p className="mt-2 font-display text-2xl text-white">
                    {REFERRAL.referralsPerYear} referrals / year
                  </p>
                  <p className="mt-1 text-slate-300 text-sm">
                    unlocks {REFERRAL.commissionPct}% lifetime
                  </p>
                </div>
              </div>
            </div>
          </section>

          <div className="text-center my-10">
            <button
              onClick={() => navigate('/waiver')}
              className="pill-btn inline-flex items-center gap-3 h-16 px-10 rounded-full bg-blue-500 hover:bg-blue-400 text-white text-lg font-bold shadow-[0_0_40px_-8px_rgba(0,168,255,0.7)]"
              data-testid="services-sign-waiver-button"
            >
              Sign the Waiver to Begin
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ServiceSection({ title, icon: Icon, services, className }) {
  return (
    <section className={className}>
      <div className="flex items-center gap-3 mb-5 px-2">
        <Icon className="w-5 h-5 text-blue-400" />
        <h2 className="font-display text-2xl sm:text-3xl text-white">{title}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {services.map((svc) => (
          <ServiceCard svc={svc} key={svc.id} />
        ))}
      </div>
    </section>
  );
}

function ServiceCard({ svc }) {
  const Icon = CATEGORY_ICONS[svc.category] || Snowflake;
  return (
    <div className="glass-card rounded-3xl p-7" data-testid={`service-card-${svc.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
            <Icon className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-blue-300 font-bold">
              {svc.subtitle}
            </p>
            <h3 className="font-display text-2xl text-white mt-0.5">{svc.name}</h3>
          </div>
        </div>
        <div className="text-right">
          <p
            className="font-display text-2xl sm:text-3xl text-blue-300 tracking-tight whitespace-nowrap"
            data-testid={`service-price-${svc.id}`}
          >
            {moneyRange(svc)}
          </p>
          <p className="text-xs text-slate-400 mt-1">{svc.duration}</p>
        </div>
      </div>
      <p className="mt-4 text-slate-300 text-[15px] leading-relaxed">
        {svc.description}
      </p>
    </div>
  );
}

const TREATMENT_FOCUS = [
  { id: 'muscle-recovery-support', label: 'Muscle Recovery Support', icon: Dumbbell },
  { id: 'skin-care', label: 'Skin Care', icon: Smile },
  { id: 'scar-age-spot', label: 'Scar & Age Spot Appearance Care', icon: Hexagon },
  { id: 'wrinkle-reduction', label: 'Wrinkle Appearance Reduction', icon: Eye },
  { id: 'body-contouring', label: 'Body Contouring', icon: Zap },
  { id: 'cellulite-smoothing', label: 'Cellulite Appearance Smoothing', icon: Waves },
  { id: 'wellness-recovery', label: 'Wellness & Recovery', icon: HeartPulse },
  { id: 'non-invasive', label: 'Non-Invasive Treatments', icon: ShieldCheck },
  { id: 'no-downtime', label: 'No Downtime', icon: Clock },
];

function WhatWeTreat({ className }) {
  return (
    <section className={className} data-testid="what-we-treat-section">
      <div className="flex items-center gap-3 mb-5 px-2">
        <Sparkles className="w-5 h-5 text-blue-400" />
        <h2 className="font-display text-2xl sm:text-3xl text-white">
          What We Treat
        </h2>
      </div>
      <div className="glass-card rounded-3xl p-6 sm:p-8">
        <p className="text-slate-300 text-[15px] leading-relaxed max-w-3xl">
          A focused menu of outcomes our cryo sessions are designed to support —
          from recovery to aesthetics.
        </p>
        <div
          className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
          data-testid="what-we-treat-grid"
        >
          {TREATMENT_FOCUS.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.id}
                className="flex items-center gap-3 p-4 rounded-2xl bg-slate-900/60 border border-blue-500/20 hover:border-blue-400/50 transition-colors"
                data-testid={`treatment-${item.id}`}
              >
                <div className="w-10 h-10 rounded-xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-blue-300" strokeWidth={1.6} />
                </div>
                <p className="text-sm sm:text-[15px] font-semibold text-slate-100 leading-snug">
                  {item.label}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}


function PackagePricingTable({ services, tiers }) {
  return (
    <div className="mt-6 -mx-4 sm:mx-0 overflow-x-auto rounded-2xl border border-blue-500/20">
      <table className="w-full text-sm text-left" data-testid="package-pricing-table">
        <thead>
          <tr className="bg-blue-500/10 border-b border-blue-500/20">
            <th className="px-4 py-3 text-xs tracking-[0.15em] uppercase text-blue-300 font-bold min-w-[220px]">
              Service · Base rate
            </th>
            {tiers.map((t) => (
              <th
                key={t.sessions}
                className="px-3 py-3 text-center whitespace-nowrap"
                data-testid={`package-tier-${t.sessions}`}
              >
                <div className="text-xs tracking-[0.15em] uppercase text-blue-300 font-bold">
                  ×{t.sessions}
                </div>
                <div className="text-[11px] text-blue-400 font-semibold mt-0.5">
                  −{t.discountPct}%
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {services.map((svc) => (
            <tr
              key={svc.id}
              className="border-b border-white/5 last:border-b-0 hover:bg-white/[0.02]"
            >
              <td className="px-4 py-3 text-slate-100">
                <div className="font-semibold whitespace-nowrap">{svc.name}</div>
                <div className="text-xs text-slate-400">
                  {svc.subtitle} · from {money(svc.priceMin)}
                </div>
              </td>
              {tiers.map((t) => {
                const bundled = svc.priceMin * t.sessions * (1 - t.discountPct / 100);
                return (
                  <td
                    key={t.sessions}
                    className="px-3 py-3 text-center whitespace-nowrap"
                  >
                    <div
                      className="font-display text-lg text-white"
                      data-testid={`bundle-price-${svc.id}-${t.sessions}`}
                    >
                      {money(bundled)}
                    </div>
                    <div className="text-[10px] text-slate-500">
                      {t.sessions} × {money(svc.priceMin * (1 - t.discountPct / 100))}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
